/**
 * Policies & Compliance — workstation, mobile, server, deployment status, results, exceptions.
 * Path sync (no React Router):
 *  /policies/workstations
 *  /policies/mobile
 *  /compliance/server-baselines
 *  /policies/deployment-status
 *  /compliance/results
 *  /compliance/exceptions
 *
 * Policy Assignments tab removed — deploy from Workstation / Mobile OS cards.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { HubPage, HubPanel, HubTabs } from '../ui/HubShell';
import { DeviceIdentityCell } from '../ui/DeviceIdentityCell';
import { apiService, Device } from '../../services/api';
import { GOVERNANCE_PATH_TO_VIEW, GOVERNANCE_VIEW_TO_PATH } from '../../config/navigation';
import { useAuth } from '../../contexts/AuthContext';
import StandardPoliciesView from '../StandardPoliciesView';
import ComplianceHub from '../ComplianceHub';
import PolicyDeploymentStatusPanel from './PolicyDeploymentStatusPanel';

type ViewId =
  | 'workstations'
  | 'mobile'
  | 'server-baselines'
  | 'deployment-status'
  | 'results'
  | 'classification'
  | 'exceptions';

const WORKSTATION_SUBCLASSES = ['desktop', 'laptop', 'kiosk', 'vdi', 'tablet_pc'];
const SERVER_SUBCLASSES = [
  'physical_server',
  'virtual_server',
  'cloud_instance',
  'database_server',
  'web_server',
  'other_server',
];

function subclassOptions(deviceClass: string): string[] {
  if (deviceClass === 'workstation') return WORKSTATION_SUBCLASSES;
  if (deviceClass === 'server') return SERVER_SUBCLASSES;
  return [];
}

function viewFromPath(): ViewId {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  // Legacy Policy Assignments URL → Workstation Policies
  if (path === '/policies/assignments') return 'workstations';
  return (GOVERNANCE_PATH_TO_VIEW[path] as ViewId) || 'workstations';
}

function pushPath(view: ViewId) {
  const path = GOVERNANCE_VIEW_TO_PATH[view];
  if (path && window.location.pathname !== path) {
    window.history.pushState({ govView: view }, '', path);
  }
}

const PAGE_META: Record<ViewId, { title: string; subtitle: string }> = {
  workstations: {
    title: 'Workstation Policies',
    subtitle: 'Configure operational control policies for desktops, laptops, and workstations.',
  },
  mobile: {
    title: 'Mobile Devices',
    subtitle: 'Android and iOS MDM policies — pick an OS card, then run or deploy controls.',
  },
  'server-baselines': {
    title: 'Server Compliance Baselines',
    subtitle: 'Server infrastructure compliance monitoring for physical, virtual, and cloud servers.',
  },
  'deployment-status': {
    title: 'Deployment Status',
    subtitle: 'Track Policies & Compliance script deploys to endpoints — queued, running, succeeded, or failed.',
  },
  results: {
    title: 'Compliance Results',
    subtitle: 'Read-only results and scoring. Not Applicable and Unsupported do not count as Failed.',
  },
  classification: {
    title: 'Device Classification',
    subtitle:
      'An unclassified device matches no policy domain. Devices classify themselves from agent data where the signals are unambiguous; the rest are listed here to be decided.',
  },
  exceptions: {
    title: 'Exceptions & Approvals',
    subtitle: 'Temporary risk acceptance and approval workflows for workstation and server controls.',
  },
};

const PoliciesComplianceHub: React.FC = () => {
  const { can } = useAuth();
  const [view, setView] = useState<ViewId>(viewFromPath());
  const [dash, setDash] = useState<any>(null);
  const [results, setResults] = useState<any[]>([]);
  const [devicesById, setDevicesById] = useState<Record<string, Device>>({});
  const [exceptions, setExceptions] = useState<any[]>([]);
  const [resultView, setResultView] = useState('all');
  const [classifications, setClassifications] = useState<any[]>([]);
  const [classifyNeedsReview, setClassifyNeedsReview] = useState(true);
  const [classifyDraft, setClassifyDraft] = useState<Record<string, { device_class: string; device_subclass: string }>>({});
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [excForm, setExcForm] = useState({
    exception_type: 'workstation_policy_exception',
    reason: '',
    risk_level: 'medium',
  });

  const go = useCallback((next: ViewId) => {
    setView(next);
    pushPath(next);
  }, []);

  useEffect(() => {
    const onPop = () => setView(viewFromPath());
    window.addEventListener('popstate', onPop);
    const path = window.location.pathname;
    if (!path.startsWith('/policies/') && !path.startsWith('/compliance/')) {
      pushPath(view);
    } else if (path === '/policies/assignments') {
      pushPath('workstations');
    }
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const reload = useCallback(async () => {
    setError(null);
    try {
      if (view === 'results') {
        setDash(await apiService.getGovernanceResultsDashboard(resultView));
        setResults(await apiService.listGovernanceResults(resultView));
        const devices = await apiService.getDevices().catch(() => []);
        const map: Record<string, Device> = {};
        (Array.isArray(devices) ? devices : []).forEach((d) => {
          if (d?.device_id) map[d.device_id] = d;
        });
        setDevicesById(map);
      }
      if (view === 'classification') {
        setClassifications(await apiService.listDeviceClassifications(classifyNeedsReview));
      }
      if (view === 'exceptions') {
        setExceptions(await apiService.listGovernanceExceptions());
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    }
  }, [view, resultView, classifyNeedsReview]);

  useEffect(() => {
    reload();
  }, [reload]);

  const meta = PAGE_META[view];
  const tabs = [
    { id: 'workstations', label: 'Workstation Policies' },
    { id: 'mobile', label: 'Mobile Devices' },
    { id: 'server-baselines', label: 'Server Compliance Baselines' },
    { id: 'deployment-status', label: 'Deployment Status' },
    { id: 'results', label: 'Compliance Results' },
    { id: 'classification', label: 'Device Classification' },
    { id: 'exceptions', label: 'Exceptions & Approvals' },
  ];

  const saveClassification = async (deviceId: string) => {
    const draft = classifyDraft[deviceId];
    if (!draft?.device_class) {
      setError('Pick a device class first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiService.classifyDevice(deviceId, {
        device_class: draft.device_class,
        device_subclass: draft.device_subclass || null,
      });
      setMsg(`Classified as ${draft.device_class}${draft.device_subclass ? ` / ${draft.device_subclass}` : ''}.`);
      setClassifyDraft(({ [deviceId]: _removed, ...rest }) => rest);
      await reload();
    } catch (e: any) {
      setError(e?.message || 'Failed to classify device');
    } finally {
      setBusy(false);
    }
  };

  return (
    <HubPage title={meta.title} subtitle={meta.subtitle}>
      <HubTabs items={tabs} value={view} onChange={(id) => go(id as ViewId)} />
      {error && <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}
      {msg && <div className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">{msg}</div>}

      {view === 'workstations' && (
        <HubPanel>
          <p className="text-xs text-slate-500 mb-3">
            Desktop / laptop / workstation controls by OS ·{' '}
            <span className="inline-block rounded bg-emerald-50 text-emerald-800 px-2 py-0.5">
              Applies to Workstations Only
            </span>
          </p>
          <p className="text-sm text-slate-600 mb-4">
            Select an OS card (Windows, macOS, Linux). Run or create a policy, then deploy to matching
            workstation devices.
          </p>
          <StandardPoliciesView allowedPlatforms={['windows', 'macos', 'linux']} />
        </HubPanel>
      )}

      {view === 'mobile' && (
        <HubPanel>
          <p className="text-xs text-slate-500 mb-3">
            Phone / tablet MDM controls by OS ·{' '}
            <span className="inline-block rounded bg-violet-50 text-violet-800 px-2 py-0.5">
              Applies to Mobile Devices Only
            </span>
          </p>
          <p className="text-sm text-slate-600 mb-4">
            Select Android or iOS, then run or deploy MDM actions (lock, wipe, profile sync, and related
            controls).
          </p>
          <StandardPoliciesView allowedPlatforms={['android', 'ios']} />
        </HubPanel>
      )}

      {view === 'server-baselines' && (
        <HubPanel>
          <p className="text-xs text-slate-500 mb-3">
            <span className="inline-block rounded bg-sky-50 text-sky-800 px-2 py-0.5">
              Applies to Servers Only
            </span>
            {' '}· Linux Hardening, Windows Server, Web Tier, Database, Cloud Assets
          </p>
          <ComplianceHub embedded />
        </HubPanel>
      )}

      {view === 'deployment-status' && (
        <HubPanel>
          <p className="text-sm text-slate-600 mb-4">
            Shows agent command outcomes for policies deployed from Workstation Policies and Mobile
            Devices. Exit code 0 with script errors (for example an empty password) is reported as
            failed, not succeeded.
          </p>
          <PolicyDeploymentStatusPanel />
        </HubPanel>
      )}

      {view === 'results' && (
        <HubPanel>
          <div className="mb-3 flex flex-wrap gap-2">
            {[
              ['all', 'All'],
              ['workstations', 'Workstations'],
              ['servers', 'Servers'],
              ['critical', 'Critical Failures'],
              ['exceptions', 'Exceptions'],
              ['trend', 'Trend Reports'],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`text-xs px-2 py-1 rounded border ${resultView === id ? 'bg-slate-900 text-white' : 'bg-white'}`}
                onClick={() => setResultView(id)}
              >
                {label}
              </button>
            ))}
          </div>
          {dash && (
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <div className="rounded border p-3">
                <div className="text-xs text-slate-500">Scored compliance %</div>
                <div className="text-2xl font-semibold">{dash.compliance_percent ?? '—'}%</div>
                <div className="text-xs text-slate-400 mt-1">
                  N/A {dash.not_applicable} · Unsupported {dash.unsupported}
                </div>
              </div>
              <div className="rounded border p-3">
                <div className="text-xs text-slate-500">Devices</div>
                <div className="text-sm mt-1">
                  WS {dash.device_counts?.workstations ?? 0} · SRV {dash.device_counts?.servers ?? 0} ·
                  Unclassified {dash.device_counts?.unclassified ?? 0}
                </div>
              </div>
              <div className="rounded border p-3 text-sm">
                Non-compliant (scored): {dash.non_compliant ?? 0}
              </div>
            </div>
          )}
          {(resultView === 'all' || resultView === 'workstations') && dash?.workstation_cards && (
            <div className="mb-4">
              <h3 className="font-medium mb-2">Workstation Compliance</h3>
              <div className="grid md:grid-cols-3 gap-2 text-sm">
                {Object.entries(dash.workstation_cards).map(([k, v]) => (
                  <div key={k} className="rounded border px-3 py-2 flex justify-between">
                    <span className="text-slate-600">{k.replace(/_/g, ' ')}</span>
                    <span>{String(v ?? '—')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(resultView === 'all' || resultView === 'servers') && dash?.server_cards && (
            <div className="mb-4">
              <h3 className="font-medium mb-2">Server Compliance</h3>
              <div className="grid md:grid-cols-3 gap-2 text-sm">
                {Object.entries(dash.server_cards).map(([k, v]) => (
                  <div key={k} className="rounded border px-3 py-2 flex justify-between">
                    <span className="text-slate-600">{k.replace(/_/g, ' ')}</span>
                    <span>{String(v ?? '—')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="py-2">Domain</th>
                <th>Check</th>
                <th>Status</th>
                <th>Device</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.id} className="border-b border-slate-100">
                  <td className="py-2">{r.policy_domain}</td>
                  <td>{r.check_name}</td>
                  <td>
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        r.status === 'Not Applicable' || r.status === 'Unsupported'
                          ? 'bg-slate-100 text-slate-600'
                          : r.status === 'Non-Compliant'
                            ? 'bg-red-50 text-red-700'
                            : 'bg-emerald-50 text-emerald-800'
                      }`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td>
                    <DeviceIdentityCell
                      device={r.device_id ? devicesById[String(r.device_id)] : undefined}
                      fallbackId={r.device_id ? String(r.device_id) : undefined}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!results.length && <p className="text-slate-400 text-sm mt-3">No evaluation results yet</p>}
        </HubPanel>
      )}

      {view === 'classification' && (
        <HubPanel>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={classifyNeedsReview}
                onChange={(e) => setClassifyNeedsReview(e.target.checked)}
              />
              Only devices needing review
            </label>
            <span className="text-xs text-slate-500">
              {classifications.length} device{classifications.length === 1 ? '' : 's'}
            </span>
          </div>

          {classifications.length === 0 ? (
            <p className="text-sm text-slate-600">
              {classifyNeedsReview
                ? 'Every device has a class. Nothing needs review.'
                : 'No devices found.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                    <th className="py-2 pr-4">Device</th>
                    <th className="py-2 pr-4">OS</th>
                    <th className="py-2 pr-4">Current</th>
                    <th className="py-2 pr-4">Suggested</th>
                    <th className="py-2 pr-4">Set class</th>
                    <th className="py-2 pr-4">Subclass</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {classifications.map((row) => {
                    const id = row.device_id;
                    const draft = classifyDraft[id] || { device_class: '', device_subclass: '' };
                    const suggestion = row.suggestion || {};
                    const suggested =
                      suggestion.device_class || suggestion.suggested_device_class || null;
                    return (
                      <tr key={id} className="border-b border-slate-100 align-top">
                        <td className="py-2 pr-4">
                          <div className="font-medium text-slate-800">{row.name || id}</div>
                          <div className="text-xs text-slate-400">{id}</div>
                        </td>
                        <td className="py-2 pr-4 text-slate-600">{row.os_type || row.os || '—'}</td>
                        <td className="py-2 pr-4">
                          {row.device_class ? (
                            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">
                              {row.device_class}
                              {row.device_subclass ? ` / ${row.device_subclass}` : ''}
                            </span>
                          ) : (
                            <span className="rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                              Unclassified
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-xs text-slate-600">
                          {suggested ? (
                            <>
                              <div>{suggested}</div>
                              <div className="text-slate-400">
                                {suggestion.confidence} confidence
                              </div>
                              {suggestion.note && (
                                <div className="text-slate-400">{suggestion.note}</div>
                              )}
                            </>
                          ) : (
                            <span className="text-slate-400">No clear signal</span>
                          )}
                        </td>
                        <td className="py-2 pr-4">
                          <select
                            className="rounded border border-slate-300 px-2 py-1 text-sm"
                            value={draft.device_class}
                            onChange={(e) =>
                              setClassifyDraft({
                                ...classifyDraft,
                                [id]: { device_class: e.target.value, device_subclass: '' },
                              })
                            }
                          >
                            <option value="">Choose…</option>
                            <option value="workstation">workstation</option>
                            <option value="server">server</option>
                            <option value="mobile">mobile</option>
                            <option value="network">network</option>
                            <option value="other">other</option>
                          </select>
                        </td>
                        <td className="py-2 pr-4">
                          <select
                            className="rounded border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-50"
                            value={draft.device_subclass}
                            disabled={subclassOptions(draft.device_class).length === 0}
                            onChange={(e) =>
                              setClassifyDraft({
                                ...classifyDraft,
                                [id]: { ...draft, device_subclass: e.target.value },
                              })
                            }
                          >
                            <option value="">—</option>
                            {subclassOptions(draft.device_class).map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2">
                          <button
                            type="button"
                            className="rounded bg-slate-800 px-3 py-1 text-sm text-white disabled:opacity-40"
                            disabled={busy || !draft.device_class}
                            onClick={() => saveClassification(id)}
                          >
                            Save
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </HubPanel>
      )}

      {view === 'exceptions' && (
        <HubPanel>
          {can('compliance_exceptions.all.request') && (
            <form
              className="mb-4 grid md:grid-cols-4 gap-2 items-end"
              onSubmit={async (e) => {
                e.preventDefault();
                setBusy(true);
                try {
                  await apiService.requestGovernanceException(excForm);
                  setExcForm({ ...excForm, reason: '' });
                  setMsg('Exception requested');
                  await reload();
                } catch (err: any) {
                  setError(err?.message || 'Request failed');
                } finally {
                  setBusy(false);
                }
              }}
            >
              <select
                className="border rounded px-2 py-1 text-sm"
                value={excForm.exception_type}
                onChange={(e) => setExcForm({ ...excForm, exception_type: e.target.value })}
              >
                <option value="workstation_policy_exception">workstation_policy_exception</option>
                <option value="server_baseline_exception">server_baseline_exception</option>
                <option value="patch_delay_exception">patch_delay_exception</option>
                <option value="port_exposure_exception">port_exposure_exception</option>
                <option value="admin_access_exception">admin_access_exception</option>
                <option value="backup_exception">backup_exception</option>
                <option value="certificate_exception">certificate_exception</option>
              </select>
              <input
                className="border rounded px-2 py-1 text-sm md:col-span-2"
                placeholder="Reason"
                value={excForm.reason}
                onChange={(e) => setExcForm({ ...excForm, reason: e.target.value })}
                required
              />
              <button className="px-3 py-1.5 rounded bg-slate-900 text-white text-sm" disabled={busy}>
                Request
              </button>
            </form>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="py-2">Type</th>
                <th>Risk</th>
                <th>Status</th>
                <th>Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {exceptions.map((ex) => (
                <tr
                  key={ex.exception_id}
                  className={`border-b border-slate-100 ${ex.is_expired ? 'bg-amber-50' : ''}`}
                >
                  <td className="py-2">{ex.exception_type}</td>
                  <td>{ex.risk_level}</td>
                  <td>{ex.status}</td>
                  <td>{ex.expires_at || '—'}</td>
                  <td className="space-x-2">
                    {ex.status === 'pending' && can('compliance_exceptions.all.approve') && (
                      <button
                        type="button"
                        className="text-xs text-emerald-700"
                        onClick={async () => {
                          await apiService.approveGovernanceException(ex.exception_id);
                          await reload();
                        }}
                      >
                        Approve
                      </button>
                    )}
                    {ex.status === 'pending' && can('compliance_exceptions.all.reject') && (
                      <button
                        type="button"
                        className="text-xs text-red-700"
                        onClick={async () => {
                          await apiService.rejectGovernanceException(ex.exception_id);
                          await reload();
                        }}
                      >
                        Reject
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!exceptions.length && <p className="text-slate-400 text-sm mt-3">No exceptions</p>}
        </HubPanel>
      )}
    </HubPage>
  );
};

export default PoliciesComplianceHub;
